import { removeLeadingZero } from 'ui/ManagerFinance/utils/utils'

/**
 * Parses and validates numeric input for text fields, allowing partial typing states.
 *
 * @param input - The input value (string or number).
 * @param onChange - Callback to handle the parsed result (string for partial input or number for valid input).
 * @param options - Optional config:
 *   - min: number | undefined – Minimum allowed value. If >= 0, disallows negatives.
 *   - allowDecimal: boolean – Whether to allow decimal points (default: true).
 */
export const parseNumericInput = (
	input: string | number,
	onChange: (value: string | number) => void,
	options?: {
		min?: number
		max?: number
		allowDecimal?: boolean
	},
) => {
	const { min, allowDecimal = true } = options ?? {}
	const raw = String(input)

	let cleaned = raw
		.replace(/,/g, '.') // replace all commas with dots
		.replace(/[^\d.-]/g, '') // Allow only digits, dash, and dot
		.replace(/(?!^)-/g, '') // Remove any dash not at the start
		.replace(/(\..*)\./g, '$1') // Keep only the first dot

	const isNegativeAllowed = min === undefined || min < 0

	const validTyping = isNegativeAllowed
		? allowDecimal
			? /^-?\d*(\.\d*)?$/
			: /^-?\d*$/
		: allowDecimal
		? /^\d*(\.\d*)?$/
		: /^\d*$/

	if (validTyping) {
		const noLeadingZeros = removeLeadingZero(cleaned)

		// Incomplete input: allow "", "." or "-" or "-."
		if (
			noLeadingZeros === '' ||
			(allowDecimal && noLeadingZeros === '.') ||
			(isNegativeAllowed &&
				(noLeadingZeros === '-' || (allowDecimal && noLeadingZeros === '-.')))
		) {
			onChange(noLeadingZeros)
			return
		}

		const characters = noLeadingZeros.split('')
		const lastCharacter = characters[characters.length - 1]

		// Special case with decimals where typing is incomplete (e.g. 245.) OR
		// typing decimal with large precision (e.g. 0.0000....5)
		if (
			allowDecimal &&
			(lastCharacter === '.' ||
				(characters.includes('.') && lastCharacter === '0'))
		) {
			onChange(noLeadingZeros)
			return
		}
		// Special case where user wants to write negative decimal above between -1 and 0
		if (isNegativeAllowed && noLeadingZeros === '-0') {
			onChange(noLeadingZeros)
			return
		}

		const num = Number(noLeadingZeros)
		// Do not enforce minimum since typing 1000 requires typing 10 first
		if (!Number.isNaN(num)) {
			// Handle max constraint if provided
			if (options?.max !== undefined && num > options.max) {
				return
			}
			onChange(num)
			return
		}
	}
}
